import { useState } from "react";
import { Users, School, ChefHat, RotateCw, CircleCheck } from "lucide-react";

const t = {
  fr: {
    title: "Résumé Exécutif",
    subtitle: "Une Plateforme. Tous les Intervenants. Impact Maximum.",
    intro: "EcoLunch est une plateforme moderne et unifiée conçue pour rationaliser les programmes de repas des écoles primaires à travers le Québec et le Canada. Contrairement aux concurrents fragmentés ou désuets, EcoLunch offre un écosystème unique et standardisé utilisé par :",
    cards: [
      { icon: Users, title: "Parents", desc: "Commander des repas, gérer les comptes et suivre les services sans effort", back: ["Inscription en 5 secondes", "Paiements sécurisés", "Notifications temps réel", "Historique complet", "Support 24/7"] },
      { icon: School, title: "Écoles", desc: "Superviser les opérations avec une visibilité et un contrôle complets", back: ["Tableau de bord admin", "Rapports quotidiens", "Gestion allergies", "Communication parents", "Zéro paperasse"] },
      { icon: ChefHat, title: "Traiteurs", desc: "Fournisseurs de services alimentaires opérant au sein d'un système puissant", back: ["Production optimisée", "Gestion multi-écoles", "Analytics revenus", "Support marketing", "Facturation auto"] },
    ],
    notPlace: "EcoLunch n'est pas une place de marché, ni un système en marque blanche, ni une collection de sites web indépendants.",
    isUnique: "C'est une plateforme unique, adoptée simultanément par tous les intervenants, créant de forts effets de réseau et une rétention élevée.",
  },
  en: {
    title: "Executive Summary",
    subtitle: "One Platform. All Stakeholders. Maximum Impact.",
    intro: "EcoLunch is a modern, unified platform designed to streamline elementary school meal programs across Quebec and Canada. Unlike fragmented or outdated competitors, EcoLunch offers a unique, standardized ecosystem used by:",
    cards: [
      { icon: Users, title: "Parents", desc: "Order meals, manage accounts, and track services effortlessly", back: ["5-second signup", "Secure payments", "Real-time notifications", "Complete history", "24/7 support"] },
      { icon: School, title: "Schools", desc: "Oversee operations with full visibility and control", back: ["Admin dashboard", "Daily reports", "Allergy management", "Parent communication", "Zero paperwork"] },
      { icon: ChefHat, title: "Caterers", desc: "Food service providers operating within a powerful system", back: ["Optimized production", "Multi-school management", "Revenue analytics", "Marketing support", "Auto billing"] },
    ],
    notPlace: "EcoLunch is not a marketplace, not a white-label system, not a collection of independent websites.",
    isUnique: "It is a single platform, adopted simultaneously by all stakeholders, creating strong network effects and high retention.",
  },
};

function FlipCard({ card }) {
  const [flipped, setFlipped] = useState(false);
  const Icon = card.icon;
  return (
    <div data-testid={`flip-card-${card.title.toLowerCase()}`} className="cursor-pointer" style={{ perspective: "1000px", minHeight: "280px" }} onClick={() => setFlipped(!flipped)}>
      <div className="relative w-full h-full transition-transform duration-500" style={{ transformStyle: "preserve-3d", transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)" }}>
        {/* Front */}
        <div className="rounded-xl border text-card-foreground shadow absolute w-full hover-lift bg-dark border-primary/30 hover:border-primary transition-all duration-300" style={{ backfaceVisibility: "hidden", minHeight: "280px" }}>
          <div className="p-6 pt-6 sm:pt-8 pb-6 sm:pb-8 text-center relative h-full flex flex-col">
            <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-primary/20 mb-4 sm:mb-6 glow-primary mx-auto">
              <Icon className="h-7 w-7 sm:h-8 sm:w-8 text-primary" />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">{card.title}</h3>
            <p className="text-sm sm:text-base text-white/70 flex-1">{card.desc}</p>
            <div className="mt-4 flex items-center justify-center gap-2 text-primary/60 text-xs sm:text-sm">
              <RotateCw className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>Cliquez pour retourner</span>
            </div>
          </div>
        </div>
        {/* Back */}
        <div className="rounded-xl border text-card-foreground shadow absolute w-full bg-dark border-primary/50 glow-primary" style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)", minHeight: "280px" }}>
          <div className="p-6 pt-6 sm:pt-8 pb-6 sm:pb-8 relative h-full flex flex-col">
            <h3 className="text-xl sm:text-2xl font-bold text-primary mb-4 sm:mb-6 text-center">{card.title}</h3>
            <ul className="space-y-2 sm:space-y-3 flex-1">
              {card.back.map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CircleCheck className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-xs sm:text-sm text-white font-medium">{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 flex items-center justify-center gap-2 text-primary/60 text-xs sm:text-sm">
              <RotateCw className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>Cliquez pour retourner</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SummarySection({ lang }) {
  const c = t[lang];
  return (
    <section id="summary" data-testid="summary-section" className="py-16 sm:py-24 bg-soft">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4" style={{ textShadow: "0 0 30px rgba(182,236,19,0.4)" }}>{c.title}</h2>
          <p className="text-lg sm:text-xl text-gradient-lime font-bold px-4">{c.subtitle}</p>
        </div>
        <div className="max-w-4xl mx-auto mb-12 sm:mb-16">
          <p className="text-base sm:text-lg text-white/90 leading-relaxed mb-8 px-4">{c.intro}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16">
          {c.cards.map((card) => (
            <FlipCard key={card.title} card={card} />
          ))}
        </div>
        <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6">
          <div className="rounded-xl border text-card-foreground shadow bg-dark border-primary/20">
            <div className="p-6 py-4 sm:py-6">
              <p className="text-base sm:text-lg text-white/90 text-center px-4">{c.notPlace}</p>
            </div>
          </div>
          <div className="rounded-xl border text-card-foreground shadow bg-dark border-primary/50 glow-strong">
            <div className="p-6 py-4 sm:py-6">
              <p className="text-base sm:text-lg font-bold text-white text-center px-4" style={{ textShadow: "0 0 30px rgba(182,236,19,0.6)" }}>{c.isUnique}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
